import { ChangeEvent, memo, useEffect, useState, FC } from "react";
import {
  Badge,
  FormControl,
  FormErrorMessage,
  FormLabel,
  Input,
  Modal,
  ModalBody,
  ModalCloseButton,
  ModalContent,
  ModalFooter,
  ModalHeader,
  ModalOverlay,
  NumberDecrementStepper,
  NumberIncrementStepper,
  NumberInput,
  NumberInputField,
  NumberInputStepper,
  Select,
  Stack,
  Tabs,
  TabList,
  TabPanels,
  Tab,
  TabPanel,
  Button,
  HStack,
  Box,
  VStack,
  Text,
} from "@chakra-ui/react";
import appconfig from "../../../config.json";
import { TenantInfo } from "../../../types/api/tenantTableTypes";
import { PrimaryButton } from "../../atoms/button/PrimaryButton";
import { useTenant } from "../../../hooks/useTenant";
import { TierPtn, TierType } from "../../../types/api/commonTypes";
import { Controller, useForm } from "react-hook-form";
import { TenantSingleParm } from "@regional-data-interconnection/common";

type Props = {
  tenant: TenantInfo | undefined;
  isOpen: boolean;
  isAdmin?: boolean;
  onClose: () => void;
};

export const TenantDetailModal: FC<Props> = memo((props) => {  const { isOpen, onClose, isAdmin = false, tenant } = props;
  // 基本設定用フォーム
  const {
    handleSubmit: handleSubmitBasic,
    register: registerBasic,
    reset: resetBasic,
    formState: { errors: errorsBasic, isSubmitting: isSubmittingBasic },
  } = useForm<TenantInfo>({
    defaultValues: tenant,
  });

  // ストレージ設定用フォーム
  const {
    handleSubmit: handleSubmitExtended,
    register: registerExtended,
    control,
    reset: resetExtended,
    formState: { errors: errorsExtended, isSubmitting: isSubmittingExtended },
  } = useForm<TenantInfo>({
    defaultValues: tenant,
  });
  const { tenantEdit, tenantDelete, tenantStorageUpdate, tenantStorageDelete, tenantResourcesUpdate, tenantResourcesDelete, loading, getTenantParam, tenantparam, getAuthorizerFunctionArn } = useTenant();
  const [tabIndex, setTabIndex] = useState(0);

  // 環境名を取得
  const envName = appconfig.IOT_MANAGEMENT_ENV.toLowerCase();

  // バケット名生成ヘルパー関数
  const generateBucketName = (tenantId: string, purpose: string) => {
    return `${envName}-${tenantId}-${purpose}`;
  };

  // デフォルトバケット名を生成
  const getDefaultBucketNames = (tenantId: string) => ({
    publicBucket: generateBucketName(tenantId, 'public'),
    privateBucket: generateBucketName(tenantId, 'private'),
    exclusiveBucket: generateBucketName(tenantId, 'exclusive'),
    anonymizedPersonalBucket: generateBucketName(tenantId, 'anonymized-personal'),
    usersWorkBucket: generateBucketName(tenantId, 'users-work'),
    binaryBucket: generateBucketName(tenantId, 'binary')
  });

  const handleTabsChange = (index: number) => {
    setTabIndex(index);
  };
    useEffect(() => {
    onModalReset();
  }, [tenant]);

  // モーダルが開いた時にテナントパラメータを取得
  useEffect(() => {
    if (isOpen && tenant?.tenant_id) {
      getTenantParam(tenant.tenant_id);
    }
  }, [isOpen, tenant?.tenant_id, getTenantParam]);

  // 基本設定の更新処理
  const onClickUpdateBasic = (data: TenantInfo) => {
    if (tenant) {
      tenant.accountName = data.accountName;
      tenant.companyName = data.companyName;
      tenant.email = data.email;
      tenant.tenantNamespace = data.tenantNamespace;
      tenant.tier = data.tier;
      
      tenantEdit(tenant);
    }
  };
  // ストレージ設定の更新処理
  const onClickUpdateExtended = (data: TenantInfo) => {
    if (tenant) {
      tenant.binaryStorageCapacityLimit = data.binaryStorageCapacityLimit || 0;
      
      // バケット情報を更新
      if (!tenant.tenantBuckets) {
        tenant.tenantBuckets = {};
      }
      
      // デフォルトバケット名を取得
      const defaultBuckets = getDefaultBucketNames(tenant.tenant_id);
      
      if (data.tenantBuckets) {
        // 空白の場合はデフォルト名を設定
        tenant.tenantBuckets.publicBucket = data.tenantBuckets.publicBucket || defaultBuckets.publicBucket;
        tenant.tenantBuckets.privateBucket = data.tenantBuckets.privateBucket || defaultBuckets.privateBucket;
        tenant.tenantBuckets.exclusiveBucket = data.tenantBuckets.exclusiveBucket || defaultBuckets.exclusiveBucket;
        tenant.tenantBuckets.anonymizedPersonalBucket = data.tenantBuckets.anonymizedPersonalBucket || defaultBuckets.anonymizedPersonalBucket;
        tenant.tenantBuckets.usersWorkBucket = data.tenantBuckets.usersWorkBucket || defaultBuckets.usersWorkBucket;
        tenant.tenantBuckets.binaryBucket = data.tenantBuckets.binaryBucket || defaultBuckets.binaryBucket;
      }
      
      tenantStorageUpdate(tenant);
    }
  };

  const handleOnError = (errors: any) => console.log(errors);
  const onClickDelete = () => {
    if (tenant?.tenant_id)
    tenantDelete(tenant?.tenant_id);
  };

  // テナントリソース更新処理
  const onClickResourceUpdate = () => {
    if (tenant?.tenant_id) {
      tenantResourcesUpdate(tenant.tenant_id);
    }
  };

  // ストレージ削除処理
  const onClickStorageDelete = () => {
    if (tenant?.tenant_id) {
      if (window.confirm('テナントストレージを削除してもよろしいですか？この操作は取り消せません。')) {
        tenantStorageDelete(tenant.tenant_id);
      }
    }
  };

  // リソース削除処理
  const onClickResourceDelete = () => {
    if (tenant?.tenant_id) {
      if (window.confirm('テナントリソースを削除してもよろしいですか？この操作は取り消せません。')) {
        tenantResourcesDelete(tenant.tenant_id);
      }
    }
  };

  // フォームリセット処理
  const onModalReset = () => {
    resetBasic(tenant);
    resetExtended(tenant);
  };

  return (
    <Modal
      isOpen={isOpen}
      onClose={()=>{onModalReset();onClose()}}
      motionPreset="slideInBottom"
      autoFocus={false}
      size="xl"
    >
      <ModalOverlay />
      <ModalContent pb={2}>
        <ModalHeader>テナント詳細</ModalHeader>
        <ModalCloseButton />
        <ModalBody mx={{base:0,md:6}}>
          {/* テナントコード(市町村)をタブの外に移動 */}
          <Box mb={4}>
            <FormControl>
              <FormLabel>テナントコード(市町村)</FormLabel>
              <Input
                id="tenant_id"
                placeholder="ID"
                value={tenant?.tenant_id}
                isReadOnly
              />
            </FormControl>
          </Box>

          <Tabs index={tabIndex} onChange={handleTabsChange} variant="enclosed">
            <TabList>
              <Tab>基本設定</Tab>
              <Tab>ストレージ設定</Tab>
              <Tab>設定値</Tab>
            </TabList>
            <TabPanels>
              <TabPanel>
                <form>
                  <Stack spacing={4}>
                    <FormControl isInvalid={Boolean(errorsBasic.accountName)}>
                      <FormLabel>表示名</FormLabel>
                      <Input
                        id="accountName"
                        placeholder="〇〇市"
                        {...registerBasic("accountName", {
                          required: "必須入力です。",
                        })}
                      />
                      <FormErrorMessage>
                        {errorsBasic.accountName && errorsBasic.accountName.message}
                      </FormErrorMessage>
                    </FormControl>
                    <FormControl>
                      <FormLabel>会社名</FormLabel>
                      <Input
                        id="companyName"
                        placeholder="companyName"
                        {...registerBasic("companyName")}
                      />
                    </FormControl>
                    <FormControl isInvalid={Boolean(errorsBasic.email)}>
                      <FormLabel>メールアドレス</FormLabel>
                      <Input
                        id="email"
                        placeholder="email"
                        {...registerBasic("email", {
                          required: false,
                          pattern: {
                            value: /^[A-Z0-9._%+-]+@[A-Z0-9.-]+\.[A-Z]{2,}$/i,
                            message: "正しいメールアドレスの形式で入力してください。",
                          },
                        })}
                      />
                      <FormErrorMessage>
                        {errorsBasic.email && errorsBasic.email.message}
                      </FormErrorMessage>
                    </FormControl>
                    <FormControl isInvalid={Boolean(errorsBasic.tenantNamespace)}>
                      <FormLabel>ID設定</FormLabel>
                      <Input
                        id="tenantNamespace"
                        placeholder="jp.xxxx.zzzz"
                        {...registerBasic("tenantNamespace", {
                          required: "必須入力です。",
                          pattern: {
                            value: /^[A-Z0-9.-]+$/i,
                            message: "ピリオド区切りの形式で入力してください。",
                          },
                        })}
                      />
                      <FormErrorMessage>
                        {errorsBasic.tenantNamespace && errorsBasic.tenantNamespace.message}
                      </FormErrorMessage>
                    </FormControl>
                    <FormControl isInvalid={Boolean(errorsBasic.tier)}>
                      <FormLabel>Plan</FormLabel>
                      <Select id="tier" placeholder="plan" {...registerBasic("tier")}>
                        <option value={TierPtn.no_personal}>Standard Tier</option>
                        <option value={TierPtn.v2}>Professional Tier</option>
                      </Select>
                      <FormErrorMessage>
                        {errorsBasic.tier && errorsBasic.tier.message}
                      </FormErrorMessage>
                    </FormControl>
                    <Box mt={4} textAlign="right">
                      <PrimaryButton onClick={handleSubmitBasic(onClickUpdateBasic, handleOnError)}>基本設定を更新</PrimaryButton>
                    </Box>
                  </Stack>
                </form>
              </TabPanel>
              <TabPanel>
                {isAdmin ? (
                  <form>
                    <Stack spacing={4}>
                      <FormControl>
                        <FormLabel>バイナリ：ストレージ制限[GB]</FormLabel>
                        <Controller
                          name="binaryStorageCapacityLimit"
                          control={control}
                          render={({ field: { ref, ...restField } }) => (
                            <NumberInput
                              {...restField}
                              precision={0}
                              step={1}
                              min={0}
                              max={1000}
                            >
                              <NumberInputField name={restField.name} />
                              <NumberInputStepper>
                                <NumberIncrementStepper />
                                <NumberDecrementStepper />
                              </NumberInputStepper>
                            </NumberInput>
                          )}
                        />
                      </FormControl>
                      <FormControl>
                        <FormLabel>テナントバケット</FormLabel>                        <Stack spacing={3}>
                          <FormControl>
                            <FormLabel fontSize="sm">公開バケット</FormLabel>
                            <Input
                              id="publicBucket"
                              placeholder={tenant ? getDefaultBucketNames(tenant.tenant_id).publicBucket : "publicBucket"}
                              {...registerExtended("tenantBuckets.publicBucket")}
                            />
                          </FormControl>
                          <FormControl>
                            <FormLabel fontSize="sm">プライベートバケット</FormLabel>
                            <Input
                              id="privateBucket"
                              placeholder={tenant ? getDefaultBucketNames(tenant.tenant_id).privateBucket : "privateBucket"}
                              {...registerExtended("tenantBuckets.privateBucket")}
                            />
                          </FormControl>
                          <FormControl>
                            <FormLabel fontSize="sm">専用バケット</FormLabel>
                            <Input
                              id="exclusiveBucket"
                              placeholder={tenant ? getDefaultBucketNames(tenant.tenant_id).exclusiveBucket : "exclusiveBucket"}
                              {...registerExtended("tenantBuckets.exclusiveBucket")}
                            />
                          </FormControl>
                          <FormControl>
                            <FormLabel fontSize="sm">匿名化個人データバケット</FormLabel>
                            <Input
                              id="anonymizedPersonalBucket"
                              placeholder={tenant ? getDefaultBucketNames(tenant.tenant_id).anonymizedPersonalBucket : "anonymizedPersonalBucket"}
                              {...registerExtended("tenantBuckets.anonymizedPersonalBucket")}
                            />
                          </FormControl>
                          <FormControl>
                            <FormLabel fontSize="sm">ユーザーワークバケット</FormLabel>
                            <Input
                              id="usersWorkBucket"
                              placeholder={tenant ? getDefaultBucketNames(tenant.tenant_id).usersWorkBucket : "usersWorkBucket"}
                              {...registerExtended("tenantBuckets.usersWorkBucket")}
                            />
                          </FormControl>
                          <FormControl>
                            <FormLabel fontSize="sm">バイナリバケット</FormLabel>
                            <Input
                              id="binaryBucket"
                              placeholder={tenant ? getDefaultBucketNames(tenant.tenant_id).binaryBucket : "binaryBucket"}
                              {...registerExtended("tenantBuckets.binaryBucket")}
                            />
                          </FormControl>
                        </Stack>
                      </FormControl>                      <Box mt={4} textAlign="right">
                        <HStack spacing={3} justifyContent="flex-end">
                          <PrimaryButton onClick={handleSubmitExtended(onClickUpdateExtended, handleOnError)}>ストレージ設定を更新</PrimaryButton>
                          {isAdmin && (
                            <Button onClick={onClickStorageDelete} colorScheme="red">ストレージ削除</Button>
                          )}
                        </HStack>
                      </Box>
                    </Stack>
                  </form>
                ) : (
                  <Box p={4} textAlign="center" color="gray.500">
                    <Text>ストレージ設定の変更はアカウント管理者のみ可能です</Text>
                  </Box>
                )}
              </TabPanel>
              <TabPanel>
                <Stack spacing={4}>
                  <Box borderWidth="1px" borderRadius="lg" p={4} bgColor="gray.50">
                    <Text fontWeight="bold">テナントID</Text>
                    <Text>{tenant?.tenant_id}</Text>
                  </Box>
                  <Box borderWidth="1px" borderRadius="lg" p={4} bgColor="gray.50">
                    <Text fontWeight="bold">Tier</Text>
                    <Text>{tenant?.tier}</Text>
                  </Box>
                  <Box borderWidth="1px" borderRadius="lg" p={4} bgColor="gray.50">
                    <Text fontWeight="bold">バイナリストレージ容量制限</Text>
                    <Text>{tenant?.binaryStorageCapacityLimit || 0} GB</Text>
                  </Box>                  <Box borderWidth="1px" borderRadius="lg" p={4} bgColor="gray.50">
                    <Text fontWeight="bold" mb={2}>テナントバケット</Text>
                    <VStack align="stretch" spacing={2}>
                      <Box>
                        <Text fontSize="sm" fontWeight="semibold">公開バケット</Text>
                        <Text fontSize="sm">{tenant?.tenantBuckets?.publicBucket || '-'}</Text>
                      </Box>
                      <Box>
                        <Text fontSize="sm" fontWeight="semibold">プライベートバケット</Text>
                        <Text fontSize="sm">{tenant?.tenantBuckets?.privateBucket || '-'}</Text>
                      </Box>
                      <Box>
                        <Text fontSize="sm" fontWeight="semibold">専用バケット</Text>
                        <Text fontSize="sm">{tenant?.tenantBuckets?.exclusiveBucket || '-'}</Text>
                      </Box>
                      <Box>
                        <Text fontSize="sm" fontWeight="semibold">匿名化個人データバケット</Text>
                        <Text fontSize="sm">{tenant?.tenantBuckets?.anonymizedPersonalBucket || '-'}</Text>
                      </Box>
                      <Box>
                        <Text fontSize="sm" fontWeight="semibold">ユーザーワークバケット</Text>
                        <Text fontSize="sm">{tenant?.tenantBuckets?.usersWorkBucket || '-'}</Text>
                      </Box>
                      <Box>
                        <Text fontSize="sm" fontWeight="semibold">バイナリバケット</Text>
                        <Text fontSize="sm">{tenant?.tenantBuckets?.binaryBucket || '-'}</Text>
                      </Box>
                    </VStack>
                  </Box>
                    <Box borderWidth="1px" borderRadius="lg" p={4} bgColor="gray.50">
                    <Text fontWeight="bold" mb={2}>環境設定値</Text>
                    <VStack align="stretch" spacing={2}>                      <Box>
                        <Text fontSize="sm" fontWeight="semibold">AuthorizerFunctionArn</Text>
                        <Text fontSize="sm" wordBreak="break-all">
                          {tenant?.tenant_id ? getAuthorizerFunctionArn(tenant.tenant_id) || '-' : '-'}
                        </Text>
                      </Box>
                    </VStack>
                  </Box>
                </Stack>
              </TabPanel>
            </TabPanels>
          </Tabs>
        </ModalBody>        <ModalFooter>
          <HStack spacing={3}>
            {isAdmin && (
              <>
                {/* 基本設定タブ（インデックス0）でのみ削除ボタンを表示 */}
                {tabIndex === 0 && (
                  <Button onClick={onClickDelete} colorScheme="red">削除</Button>
                )}                {/* 設定値タブ（インデックス2）でのみリソース更新・削除ボタンを表示 */}
                {tabIndex === 2 && (
                  <>
                    <Button onClick={onClickResourceUpdate} colorScheme="blue" isLoading={loading}>
                      リソース更新
                    </Button>
                    <Button onClick={onClickResourceDelete} colorScheme="red">
                      リソース削除
                    </Button>
                  </>
                )}
              </>
            )}
          </HStack>
        </ModalFooter>
      </ModalContent>
    </Modal>
  );
});
